import {
  Box,
  Button,
  DebtRepaymentPlan,
  DebtRepaymentService,
  Disclaimer,
  Div,
  H3,
  H5,
  P,
  Separator,
  TextButton,
  displayCentsInDollars,
  lightTheme,
} from '@vgrubs/components';
import { formatDay } from '../helpers/receivablesLabels';
import {
  SERVICE_DEBT_NAMES,
  SERVICE_PLAN_TITLES,
} from '../helpers/repaymentPlanLabels';
import { PlanMethodChips } from './PlanMethodChips';
import { ProgressFill, ProgressTrack } from './RepaymentPlanCard.styled';
import { StatusBadge } from './ReceivablesCells';

type Props = {
  service: DebtRepaymentService;
  /** This service's active plan. */
  plan: DebtRepaymentPlan | null;
  /** What this service's plan can repay now. */
  repayableDebt: number;
  /** SaaS only: the vDrive plan runs too and takes first. */
  runsAfterVDrive?: boolean;
  /** Admin only: support sees the plan, not the buttons. */
  canEdit: boolean;
  onCreate: () => void;
  onEdit: (plan: DebtRepaymentPlan) => void;
  onStop: (plan: DebtRepaymentPlan) => void;
  /** This service's plans collected something: the history link shows. */
  hasRepayments: boolean;
  onShowRepayments: () => void;
  /** Stopped and completed plans; the link only shows past 0. */
  pastPlansCount: number;
  onShowPastPlans: () => void;
};

/**
 * One of the location's debt repayment plans (vDrive or SaaS): what it
 * collects and how far along it is, or the way to start one while there is
 * old debt of that service to repay. The collections and the plans before
 * this one open from "Repayment history" and "Past plans" in modals: as lists
 * on the page they pushed everything under them out of the screen.
 */
export const RepaymentPlanCard = ({
  service,
  plan,
  repayableDebt,
  runsAfterVDrive = false,
  canEdit,
  onCreate,
  onEdit,
  onStop,
  hasRepayments,
  onShowRepayments,
  pastPlansCount,
  onShowPastPlans,
}: Props) => {
  const title = SERVICE_PLAN_TITLES[service];
  // Links to lists, not actions on the plan: right-aligned on the card's last
  // line, or under Create in the no-plan state. Each only once it has rows.
  const history =
    hasRepayments || pastPlansCount > 0 ? (
      <Div $flex $alignCenter $gap="16px" $flexNone>
        {pastPlansCount > 0 && (
          <TextLink onClick={onShowPastPlans}>Past plans</TextLink>
        )}
        {hasRepayments && (
          <TextLink onClick={onShowRepayments}>Repayment history</TextLink>
        )}
      </Div>
    ) : null;

  if (!plan) {
    return (
      // No plan: the title and what can be done on the left as one block,
      // Create and the history link stacked on the right as another,
      // centred against the whole left side.
      <Box $flex $alignCenter $between $gap="16px" $columnSM $alignStartSM>
        <Div $flex $column $gap="12px" $minWidth="0">
          <H5 $m="0">{title}</H5>
          {repayableDebt > 0 ? (
            <Div>
              <P $m="0">
                {displayCentsInDollars(repayableDebt)} of{' '}
                {SERVICE_DEBT_NAMES[service]} can be paid back in small amounts:
                from the vGrubs balance, per order or per delivery.
              </P>
              <Disclaimer $m="0" $color={lightTheme.lightGray}>
                {service === 'saas'
                  ? 'No plan is running. Only fees billed before this month; newer fees go through ACH as usual.'
                  : 'No plan is running. The vDrive reserve is not part of a plan.'}
              </Disclaimer>
            </Div>
          ) : (
            <Disclaimer $m="0" $color={lightTheme.lightGray}>
              No {SERVICE_DEBT_NAMES[service]} to repay.
            </Disclaimer>
          )}
        </Div>
        {((canEdit && repayableDebt > 0) || history) && (
          <Div
            $flex
            $column
            $alignEnd
            $alignStartSM
            $gap="8px"
            $flexNone
            $widthSM="100%"
          >
            {canEdit && repayableDebt > 0 && (
              <Button
                $small
                $selected
                $w="220px"
                $wSM="100%"
                onClick={onCreate}
              >
                Create repayment plan
              </Button>
            )}
            {history}
          </Div>
        )}
      </Box>
    );
  }

  return (
    <Box $flex $column $gap="12px">
      {/* The title and what the plan collects with are one block, so the
          buttons beside it neither push the methods down nor leave a gap
          under the title; they sit centred against the whole block. */}
      <Div $flex $alignCenter $between $gap="16px">
        <Div $flex $column $gap="8px" $minWidth="0">
          <Div $flex $alignCenter $gap="12px">
            <H5 $m="0">{title}</H5>
            <StatusBadge color={lightTheme.green}>Active</StatusBadge>
          </Div>
          <PlanMethodChips plan={plan} />
        </Div>
        {canEdit && (
          <Div $desktop $flexNone>
            <PlanActions plan={plan} onEdit={onEdit} onStop={onStop} />
          </Div>
        )}
      </Div>

      <ActivePlan
        plan={plan}
        repayableDebt={repayableDebt}
        runsAfterVDrive={runsAfterVDrive}
        history={history}
      />
      {/* On a phone the buttons close the card instead: in the header they
          wrapped between the title and what they act on. */}
      {canEdit && (
        <Div $mobile $mt="4">
          <PlanActions plan={plan} onEdit={onEdit} onStop={onStop} />
        </Div>
      )}
    </Box>
  );
};

const TextLink = ({
  onClick,
  children,
}: {
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <TextButton
    color={lightTheme.primary}
    $underline
    style={{ width: 'auto', height: 'auto', flex: 'none' }}
    onClick={onClick}
  >
    {children}
  </TextButton>
);

const PlanActions = ({
  plan,
  onEdit,
  onStop,
}: {
  plan: DebtRepaymentPlan;
  onEdit: (plan: DebtRepaymentPlan) => void;
  onStop: (plan: DebtRepaymentPlan) => void;
}) => (
  <Div $flex $gap="8px">
    <Button
      $small
      $w="100px"
      $wSM="calc(50% - 4px)"
      $selected
      onClick={() => onEdit(plan)}
    >
      Edit
    </Button>
    <Button
      $small
      $w="100px"
      $wSM="calc(50% - 4px)"
      $gray
      $selected
      onClick={() => onStop(plan)}
    >
      Stop plan
    </Button>
  </Div>
);

const ActivePlan = ({
  plan,
  repayableDebt,
  runsAfterVDrive,
  history,
}: {
  plan: DebtRepaymentPlan;
  repayableDebt: number;
  runsAfterVDrive: boolean;
  /** The "Past plans" / "Repayment history" links, on the Started line's right. */
  history: React.ReactNode;
}) => {
  // What the plan collected against what it collected plus what is left:
  // the debt it started with, give or take items added or closed since.
  const whole = plan.repaidAmount + repayableDebt;
  const percent = whole > 0 ? Math.round((plan.repaidAmount / whole) * 100) : 0;

  return (
    <>
      <Separator />

      <Div $flex $between $gap="16px" $alignEnd>
        <Div>
          <Disclaimer $m="0">Repaid</Disclaimer>
          <H3 $m="0" $color={lightTheme.green}>
            {displayCentsInDollars(plan.repaidAmount)}
          </H3>
        </Div>
        <Div>
          <Disclaimer $m="0" $right>
            Left to repay
          </Disclaimer>
          <H3 $m="0" $right $color={lightTheme.primary}>
            {displayCentsInDollars(repayableDebt)}
          </H3>
        </Div>
      </Div>
      <ProgressTrack>
        <ProgressFill $percent={percent} />
      </ProgressTrack>

      {/* One block, closer together than the card's 12px rhythm: the note
          belongs to the line above it. */}
      <Div $flex $column $gap="4px">
        <Div $flex $alignCenter $between $gap="16px">
          <Disclaimer $m="0" $color={lightTheme.lightGray}>
            Started {formatDay(plan.createdAt)}. Oldest charge first; the plan
            completes on its own once the debt is paid off.
            {runsAfterVDrive &&
              ' Runs after the vDrive plan, on what that one leaves.'}
          </Disclaimer>
          {history}
        </Div>
        {plan.note && <Disclaimer $m="0">Note: {plan.note}</Disclaimer>}
      </Div>
    </>
  );
};
